import React, { useState, useEffect, useRef } from 'react';

const PartnershipComponent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [yearsCount, setYearsCount] = useState(0);
  const [satisfactionCount, setSatisfactionCount] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const componentRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const animateValue = (start, end, duration, setter) => {
    if (start === end) return;
    const range = end - start;
    const increment = end > start ? 1 : -1;
    const stepTime = Math.abs(Math.floor(duration / range));
    
    let current = start;
    const timer = setInterval(() => {
      current += increment;
      setter(current);
      if (current === end) {
        clearInterval(timer);
      }
    }, stepTime);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          animateValue(0, 15, 1500, setYearsCount);
          animateValue(0, 100, 2000, setSatisfactionCount);
        }
      },
      { threshold: 0.2 }
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, []);

  const styles = {
    container: {
      // backgroundColor: '#f8f9fa',
      padding: windowWidth <= 768 ? '20px 15px' : '40px 20px',
      fontFamily: 'Poppins, sans-serif',
      display: 'flex',
      flexDirection: windowWidth <= 1024 ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: windowWidth <= 1024 ? 'center' : 'flex-start',
      minHeight: windowWidth <= 768 ? 'auto' : '400px',
      boxShadow: 'inset 0 0 100px rgba(0,0,0,0.03)',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      maxWidth: '1400px',
      margin: '0 auto',
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden',
    },

    leftSection: {
      flex: windowWidth <= 1024 ? 'none' : '1',
      width: windowWidth <= 1024 ? '100%' : 'auto',
      maxWidth: windowWidth <= 1024 ? '100%' : '550px',
      padding: windowWidth <= 768 ? '10px' : '20px',
      opacity: isVisible ? 1 : 0,
      marginLeft:'40px',
      transform: isVisible ? 'translateX(0)' : 'translateX(-50px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      transitionDelay: '0.2s',
      marginBottom: windowWidth <= 1024 ? '30px' : '0',
    },

    aboutLabel: {
      fontSize: windowWidth <= 480 ? '12px' : '14px',
      fontWeight: '600',
      color: '#400684',
      letterSpacing: '1.5px',
      marginBottom: '15px',
      display: 'block',
    },

    heading: {
      fontSize: windowWidth <= 480 ? '16px' : windowWidth <= 768 ? '18px' : '40px', // Mobile: 16px, Tablet: 18px, Laptop/Desktop: 20px
      marginBottom: '25px',
      fontFamily: 'Oswald, sans-serif',
      color: '#414042',
      lineHeight: '1.2',
      fontWeight: '700',
      wordWrap: 'break-word',
    },
    
    titleSpan: {
      color: '#400684',
      fontFamily: 'Oswald, sans-serif',
      display: 'block',
      lineHeight:'40px',
      fontWeight: '600',
      marginTop: '5px',
      fontSize: windowWidth <= 480 ? '28px' : windowWidth <= 768 ? '36px' : '40px', // Mobile: 28px, Tablet: 36px, Laptop/Desktop: 42px
    },
    

    description: {
      fontSize: windowWidth <= 480 ? '15px' : '16px',
      lineHeight: '24px',
      fontWeight:'400',
      color: '#4a4a4a',
      fontFamily: 'Inter, sans-serif',
      marginBottom: '30px',
    },

    rightSection: {
      flex: windowWidth <= 1024 ? 'none' : '1',
      width: windowWidth <= 1024 ? '100%' : 'auto',
      display: 'flex',
      flexDirection: windowWidth <= 768 ? 'column' : 'row',
      justifyContent: 'center',
      alignItems: 'center',
      gap: windowWidth <= 768 ? '20px' : '25px',
      padding: windowWidth <= 768 ? '10px' : '20px',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0)' : 'translateX(50px)',
      transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
      transitionDelay: '0.4s',
    },

    imageCard: {
      borderRadius: '15px',
      overflow: 'hidden',
      width: windowWidth <= 768 ? '100%' : '280px',
      maxWidth: windowWidth <= 768 ? '100%' : '400px',
      margin: windowWidth <= 768 ? '0 auto' : '0',
    },

    image: {
      width: '100%',
      height: windowWidth <= 480 ? '250px' : '290px',
      objectFit: 'cover'
    },


    statsCard: {
      backgroundColor: 'white',
      borderRadius: '15px',
      padding: windowWidth <= 480 ? '15px' : '20px',
      color: 'black',
      width: windowWidth <= 768 ? '100%' : '280px',
      maxWidth: windowWidth <= 768 ? '100%' : '400px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      margin: windowWidth <= 768 ? '0 auto' : '0',
      boxSizing: 'border-box',
    },

    partnerLogos: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '25px',
      position: 'relative',
      flexWrap: 'wrap',
      gap: '5px',
    },

    logo: {
      width: windowWidth <= 480 ? '35px' : '40px',
      height: windowWidth <= 480 ? '35px' : '40px',
      borderRadius: '50%',
      marginRight: '-15px',
      border: '3px solid white',
      backgroundColor: 'white',
      boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
      objectFit: 'contain',
      padding: '5px',
    },

    partnerLabel: {
      marginLeft: '30px',
      fontSize: windowWidth <= 480 ? '13px' : '15px',
      fontFamily: 'Oswald, sans-serif',
      color: '#666666',
      fontWeight: '500',
    },

    stats: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: '20px',
      padding: '20px 0',
      borderTop: '1px solid #eee',
      flexWrap: 'wrap',
      gap: '20px',
    },

    statItem: {
      textAlign: 'left',
      flex: windowWidth <= 480 ? '0 0 100%' : '1',
    },

    statNumber: {
      fontSize: windowWidth <= 480 ? '28px' : '32px',
      fontWeight: '700',
      color: '#400684',
      marginBottom: '8px',
    },

    statLabel: {
      fontSize: windowWidth <= 480 ? '13px' : '15px',
      color: '#414042',
      fontFamily: 'Oswald, sans-serif',
      fontWeight: '500',
    },
  };

  return (
    <div style={styles.container} ref={componentRef}>
      <div style={styles.leftSection}>
        <span style={styles.aboutLabel}></span>
        <h1 style={styles.heading}>
          EJIL ENTERPRISE
          <span style={styles.titleSpan}>PARTNERSHIP BENEFITS</span>
        </h1>
        <p style={styles.description}>
          At Ejil Enterprise, we pioneer innovative telecommunications solutions that 
          transform businesses. Our partnership program delivers cutting-edge technology, 
          comprehensive support, and exceptional growth opportunities. Join our elite 
          network of partners and experience the future of business communications.
        </p>
      </div>

      <div style={styles.rightSection}>
        <div style={styles.imageCard}>
          <img 
            src="/partner1.jpg"
            alt="Professional telecommunications solutions"
            style={styles.image}
          />
        </div>

        <div style={styles.statsCard}>
          <div>
            <div style={styles.partnerLogos}>
              <img src="/ipfone.png" alt="IPFone" style={styles.logo} />
              <img src="/ringcentral.svg" alt="RingCentral" style={styles.logo} />
              <img src="/NEC.png" alt="NEC" style={styles.logo} />
              <span style={styles.partnerLabel}>TRUSTED PARTNERS</span>
            </div>
          </div>

          <div style={styles.stats}>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>{yearsCount}+</div>
              <div style={styles.statLabel}>YEARS EXPERIENCE</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>{satisfactionCount}%</div>
              <div style={styles.statLabel}>CLIENT SATISFACTION</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnershipComponent;